import { get, post } from '../scripts/lib/ajax';

class Cart {
  /**
   * Handles errors for Shopify's add/change endpoints, and alert the user an error occurred
   * @param {object} e - API http/network request error object
   */
  handleCartError(e) {
    let errorMsg = 'An error occurred while trying to add/change quantity of cart item.';

    if (e.status && e.status === 422) {
      errorMsg = `${e.message}: ${e.description}`;
    }
    console.log('Handle Cart Error', e);
    console.error(errorMsg);
  }

  /**
   * Add an item to cart using variantId and quantity
   * @param {integer} quantity
   * @param {integer} variantId
   * @param {object} properties
   * @param {string} sellingPlan
   * @returns {object} returns the product line item data of the product added to cart
   */
  add(quantity, variantId, properties = null, sellingPlan) {
    return new Promise((resolve) => {
      // Shopify has to have the X-requested-with header to be able to return 422 only on /add.js endpoint
      // https://community.shopify.com/c/Shopify-Design/AJAX-POST-cart-add-js-NEVER-returns-422-only-200-OK-on/td-p/375736
      const headers = {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      };

      const data = {
        quantity,
        id: variantId,
        selling_plan: sellingPlan,
      };

      if (properties) {
        data.properties = properties;
      }

      post('/cart/add.js', data, headers)
        .then((res) => {
          resolve(res);
        })
        .catch((e) => {
          this.handleCartError(e);
          resolve(null);
        });
    });
  }

  /**
   * Change a cart line item using the line item key and the quantity you want to change it to.
   * @param {integer} quantity
   * @param {string} key 32 character long alphanumeric string
   * @returns {object} returns new cart data reflecting the change
   */
  change(quantity, key) {
    const data = { id: key, quantity };

    return new Promise((resolve, reject) => {
      post('/cart/change.js', data)
        .then((res) => {
          resolve(res);
        })
        .catch((e) => {
          this.handleCartError(e);
          resolve(null);
        });
    });
  }

  /**
   * Change a cart line item using the line item key and the selling plan you want to change it to.
   * @param {string} selling_plan_id
   * @param {string} key 32 character long alphanumeric string
   * @param {integer} quantity
   * @returns {object} returns new cart data reflecting the change
   */
  changeSellingPlan(selling_plan_id, key, quantity) {
    const data = { id: key, selling_plan: selling_plan_id, quantity };

    return new Promise((resolve, reject) => {
      post('/cart/change.js', data)
        .then((res) => {
          resolve(res);
        })
        .catch((e) => {
          this.handleCartError(e);
          resolve(null);
        });
    });
  }

  /**
   *  Fetches cart data and joins other product data into one object
   * @returns {object} returns cart data in addition to some product data Shopify doesn't provide
   */
  fetchNewCart() {
    return new Promise((resolve, reject) => {
      get('/cart.js')
        .then(async (res) => {
          const cartMeta = await get('/cart?view=meta');
          const cart = res;
          this.count = cart.item_count;
          cart.has_subscription = false;
          cart.checkout_charge_amount = cartMeta.checkout_charge_amount;

          cart.items.forEach((cartItem) => {
            const productMeta = cartMeta.items.find((el) => el.key === cartItem.key);
            cartItem.product_title = productMeta?.product_title;
            cartItem.international_variant_title = productMeta?.title;
            cartItem.compare_at_price = productMeta?.compare_at_price;
            cartItem.line_compare_at_price = productMeta?.compare_at_price
              ? productMeta.compare_at_price * cartItem.quantity
              : null;

            if (productMeta?.selling_plan_allocation) {
              cartItem.selling_plan_allocation = {
                checkout_charge_amount: productMeta.selling_plan_allocation.checkout_charge_amount,
                compare_at_price: productMeta.selling_plan_allocation.compare_at_price,
                per_delivery_price: productMeta.selling_plan_allocation.per_delivery_price,
                price: productMeta.selling_plan_allocation.price,
                remaining_balance_charge_amount: productMeta.selling_plan_allocation.remaining_balance_charge_amount,
                selling_plan: productMeta.selling_plan_allocation.selling_plan,
                selling_plan_group_id: productMeta.selling_plan_allocation.selling_plan_group_id,
                unit_price: productMeta.selling_plan_allocation.unit_price,
              };
            } else {
              cartItem.selling_plan_allocation = null;
            }
          });

          resolve(cart);
        })
        .catch((e) => {
          console.error(`Couldn't get cart data.`, e);
          resolve(null);
          // alert('An error occurred while trying to get new cart data.')
        });
    });
  }

  /**
   * Clears the cart of all items
   * @returns {object} returns empty cart data
   */
  clear() {
    return new Promise((resolve) => {
      post('/cart/clear.js')
        .then((res) => {
          resolve(res);
        })
        .catch((e) => {
          console.log('An error occurred while trying to clear cart.', e);
          alert('Error occurred trying to clear cart.');
          resolve(null);
        });
    });
  }
}

const shopifyCart = new Cart();
export { shopifyCart, Cart };
